/**
 * Shared chart geometry. No color, no React, no formatting.
 *
 * Public (V3Chart), admin (AChart), and print documents (CMA / packets)
 * draw from this file so a series is the same line, bar, or mix everywhere.
 * value is Y geometry only. Every label and tick is a string the caller
 * already formatted. A cubic would invent values between the points.
 */

export const VB_W = 320
export const VB_H = 140
export const MIX_H = 36
export const PAD = { l: 2, r: 2, t: 8, b: 8 }

/**
 * Where a plot-relative fraction lands in the SVG BOX, as a percentage.
 *
 * A tick's `frac` runs 0 to 1 across the plot area, which is inset from the
 * viewBox by PAD. The rails are HTML beside the SVG, and applying the fraction
 * to the rail's own full height put the top label 31.7px above the gridline it
 * names (evaluator round five, SUBDIVISION-CHART-2). This maps one to the
 * other.
 */
export function bandTopPct(frac: number, vbH: number = VB_H, padB: number = PAD.b): number {
  const h = vbH - PAD.t - padB
  return ((PAD.t + frac * h) / vbH) * 100
}

/** A vertical bar chart reserves more room under its baseline than a line. */
export const BAR_PAD_B = 18

export function bandLeftPct(frac: number, vbW: number = VB_W): number {
  const w = vbW - PAD.l - PAD.r
  return ((PAD.l + frac * w) / vbW) * 100
}

export type ChartKind = 'line' | 'bars' | 'mix'

export type PlotPointIn = {
  value: number
  label: string
  tick: string
  at?: number
}

export type PlotSeriesIn = {
  name: string
  points: readonly PlotPointIn[]
}

export type PlottedPoint = {
  x: number
  y: number
  plot: boolean
  label: string
  tick: string
}

/** A y tick the caller formatted: where it sits and what it says. */
export type PlotTickIn = { value: number; label: string }
/** An x tick keyed the way the points are (`at`, or order when no `at`). */
export type PlotXTickIn = { at: number; label: string }

export type LinePlot = {
  kind: 'line'
  lines: { name: string; d: string; points: PlottedPoint[] }[]
  /** The plot box in viewBox units and the value scale that filled it. */
  scale: { l: number; t: number; w: number; h: number; y0: number; y1: number; xMin: number; xMax: number; useAt: boolean }
  /** Threshold zones behind the lines, clamped to the data's y-domain. */
  bands: { y: number; h: number; label: string }[]
  yMinLabel: string
  yMaxLabel: string
  xStart: string
  xEnd: string
  vbW: number
  vbH: number
}

export type BarRect = {
  name: string
  tick: string
  label: string
  x: number
  y: number
  w: number
  h: number
  index: number
  highlight: boolean
}

export type BarPlot = {
  kind: 'bars'
  layout: 'vertical' | 'horizontal'
  bars: BarRect[]
  yMinLabel: string
  yMaxLabel: string
  ticks: { tick: string; x: number; y: number }[]
  /** Vertical rule through a bar chart (this home's price). SVG x. */
  ref?: { x: number; label: string }
  vbW: number
  vbH: number
}

export type MixSegment = {
  name: string
  tick: string
  label: string
  x: number
  w: number
  index: number
}

export type MixPlot = {
  kind: 'mix'
  segments: MixSegment[]
  totalLabel: string
  vbW: number
  vbH: number
}

/** A horizontal value band (threshold zone) mapped into the line frame. */
export type LineBand = {
  y: number
  h: number
  label: string
}

/** One row of a range plot: a lollipop dot, or a dumbbell pair. Percent geometry. */
export type RangeRowIn = {
  /** Row name (a town, a segment). */
  tick: string
  /** The primary value — the filled dot. */
  value: number
  /** The primary value as the caller formatted it. */
  label: string
  /** Optional prior/context value — makes the row a dumbbell. */
  baseValue?: number
  /** The prior value as the caller formatted it. */
  baseLabel?: string
  /** Context for the reading (population, sample size). Pass-through, no geometry. */
  note?: string
  /**
   * Rows the primary figure was computed over. Pass-through, no geometry —
   * the atom formats and draws it. See V3ChartRangeRow.sample for the rule
   * governing when a caller may supply this at all.
   */
  sampleN?: number
  /** Rows the BASE (prior) figure was computed over, on a dumbbell row. */
  sampleBaseN?: number
}

export type RangeBandIn = {
  from: number
  to: number
  label: string
}

export type RangePlotRow = {
  tick: string
  /** Primary dot position, percent of the track (0–100). */
  xPct: number
  label: string
  /** Dumbbell tail position, percent; null on a lollipop row. */
  baseXPct: number | null
  baseLabel: string | null
  /** Stem span, percent. Lollipop stems rise from the domain floor. */
  stemStartPct: number
  stemEndPct: number
  /** Context for the reading, as the caller passed it. */
  note: string | null
  /** Rows the primary figure was computed over; null when unpublished. */
  sampleN: number | null
  /** Rows the base figure was computed over; null on a lollipop row. */
  sampleBaseN: number | null
  /**
   * True when the row's value sits beyond opts.clampMax and is drawn AT the
   * clamp instead of at scale (the chart-room broken-bar rule: one 36-month
   * outlier must not compress every other row into the left tenth of the
   * track). The label still carries the true reading.
   */
  clamped: boolean
  index: number
}

export type RangePlot = {
  kind: 'range'
  rows: RangePlotRow[]
  bands: { xPct: number; wPct: number; label: string }[]
  /** Vertical reference rule (e.g. the region figure); null when out of domain. */
  ref: { xPct: number; label: string } | null
  xMinLabel: string
  xMaxLabel: string
}

export type AnyPlot = LinePlot | BarPlot | MixPlot | RangePlot

function isFiniteNumber(value: number): boolean {
  return Number.isFinite(value)
}

/**
 * A sample size, or null. A negative or fractional count is not a count of
 * rows, and drawing one would put a number on the page that no query returned.
 */
function countOrNull(value: number | undefined): number | null {
  return value != null && Number.isInteger(value) && value >= 0 ? value : null
}

export function linePath(points: readonly PlottedPoint[]): string {
  let d = ''
  let drawing = false
  for (const p of points) {
    if (!p.plot) {
      drawing = false
      continue
    }
    const cmd = drawing ? 'L' : 'M'
    d += `${cmd}${p.x.toFixed(2)},${p.y.toFixed(2)} `
    drawing = true
  }
  return d.trim()
}

/**
 * Straight segments. The line lifts across a gap. No spline.
 *
 * `headroom` widens the value domain past the data's own top and bottom, as a
 * fraction of the data's span (0.06 each side when omitted, the air every line
 * has always had). A chart that labels its high above the line and its low
 * below it (V3Chart `callouts`) asks for more on that side, so the label sits
 * inside the drawing instead of over the reading band or the axis. Form only:
 * the points and their values are unchanged, the scale gives them more room.
 */
export function buildLinePlot(
  series: readonly PlotSeriesIn[],
  opts?: { bands?: readonly RangeBandIn[]; headroom?: { top?: number; bottom?: number } },
): LinePlot | null {
  const useAt = series.some((s) => s.points.some((p) => p.at != null && isFiniteNumber(p.at)))

  const finite: { point: PlotPointIn; order: number }[] = []
  series.forEach((s) => {
    s.points.forEach((point, order) => {
      if (!isFiniteNumber(point.value)) return
      if (useAt && (point.at == null || !isFiniteNumber(point.at))) return
      finite.push({ point, order })
    })
  })
  if (finite.length < 2) return null

  let xMin = Infinity
  let xMax = -Infinity
  let yMin = Infinity
  let yMax = -Infinity
  let yMinLabel: string | null = null
  let yMaxLabel: string | null = null
  let xStart: string | null = null
  let xEnd: string | null = null
  let xStartAt = Infinity
  let xEndAt = -Infinity

  const takeX = (xKey: number, tick: string) => {
    if (xKey < xMin) xMin = xKey
    if (xKey > xMax) xMax = xKey
    if (xKey < xStartAt) {
      xStartAt = xKey
      xStart = tick
    }
    if (xKey > xEndAt) {
      xEndAt = xKey
      xEnd = tick
    }
  }

  // X domain is the calendar (or the declared `at` keys), including months
  // with no y. Dropping them lets a later priced month slide into their slot.
  series.forEach((s) => {
    s.points.forEach((point, order) => {
      if (useAt) {
        if (point.at == null || !isFiniteNumber(point.at)) return
        takeX(point.at, point.tick)
        return
      }
      takeX(order, point.tick)
    })
  })

  for (const row of finite) {
    if (row.point.value < yMin) {
      yMin = row.point.value
      yMinLabel = row.point.label
    }
    if (row.point.value > yMax) {
      yMax = row.point.value
      yMaxLabel = row.point.label
    }
  }
  if (yMinLabel == null || yMaxLabel == null || xStart == null || xEnd == null) return null

  const ySpan = yMax - yMin || 1
  const padTop = ySpan * Math.max(0.06, opts?.headroom?.top ?? 0.06)
  const padBottom = ySpan * Math.max(0.06, opts?.headroom?.bottom ?? 0.06)
  const y0 = yMin - padBottom
  const y1 = yMax + padTop
  const yRange = y1 - y0 || 1
  const xSpan = xMax - xMin || 1
  const plotW = VB_W - PAD.l - PAD.r
  const plotH = VB_H - PAD.t - PAD.b

  const xOf = (xKey: number) => PAD.l + ((xKey - xMin) / xSpan) * plotW
  const yOf = (value: number) => PAD.t + (1 - (value - y0) / yRange) * plotH

  const lines: LinePlot['lines'] = []
  series.forEach((s) => {
    const plotted: PlottedPoint[] = s.points.map((point, order) => {
      const hasAt = !useAt || (point.at != null && isFiniteNumber(point.at))
      const plot = isFiniteNumber(point.value) && hasAt
      const xKey = useAt && point.at != null ? point.at : order
      return {
        x: hasAt ? xOf(xKey) : 0,
        y: plot ? yOf(point.value) : 0,
        plot,
        label: point.label,
        tick: point.tick,
      }
    })
    const plotCount = plotted.filter((p) => p.plot).length
    if (plotCount < 2) return
    const d = linePath(plotted)
    if (!d) return
    lines.push({ name: s.name, d, points: plotted })
  })
  if (lines.length === 0) return null

  // Threshold zones, clamped to the data's y-domain. A band is context for
  // values that exist; it never widens the domain, and one that falls wholly
  // outside the plotted range is dropped rather than distorting the scale.
  const bands: LinePlot['bands'] = []
  for (const band of opts?.bands ?? []) {
    if (!isFiniteNumber(band.from) || !isFiniteNumber(band.to)) continue
    const lo = Math.max(Math.min(band.from, band.to), y0)
    const hi = Math.min(Math.max(band.from, band.to), y1)
    if (!(hi > lo)) continue
    const top = yOf(hi)
    const bottom = yOf(lo)
    bands.push({ y: top, h: bottom - top, label: band.label })
  }

  return {
    kind: 'line',
    lines,
    scale: { l: PAD.l, t: PAD.t, w: plotW, h: plotH, y0, y1, xMin, xMax, useAt },
    bands,
    yMinLabel,
    yMaxLabel,
    xStart,
    xEnd,
    vbW: VB_W,
    vbH: VB_H,
  }
}

/**
 * Lollipop / dumbbell rows: categories on Y, one value (dot) or a pair
 * (prior -> current) on X. Geometry is PERCENT of the track so the renderer
 * lays rows out in HTML and circles stay circles at any width — a stretched
 * SVG viewBox would draw them as ellipses.
 *
 * Domain: with any dumbbell pair present, the padded extent of all values;
 * lollipop-only rows with a non-negative floor anchor at zero so stem length
 * is magnitude, not an artifact of the minimum.
 */
export function buildRangePlot(
  rows: readonly RangeRowIn[],
  opts?: {
    bands?: readonly RangeBandIn[]
    /**
     * Domain ceiling. A value beyond it is drawn AT the ceiling with
     * `clamped: true` instead of stretching the scale (one degenerate
     * outlier — a 36-month supply on 6 actives — must not flatten every
     * honest row). The row's label still states the true value.
     */
    clampMax?: number
    /** Vertical reference rule (a region figure, full ask). Dropped when it falls outside the domain. */
    refValue?: number
    refLabel?: string
  },
): RangePlot | null {
  const usable = rows.filter((r) => isFiniteNumber(r.value))
  if (usable.length < 1) return null

  const clampMax =
    opts?.clampMax != null && isFiniteNumber(opts.clampMax) ? opts.clampMax : null
  const clampOf = (value: number) => (clampMax != null ? Math.min(value, clampMax) : value)

  const hasBase = usable.some((r) => r.baseValue != null && isFiniteNumber(r.baseValue))
  let min = Infinity
  let max = -Infinity
  let minLabel = usable[0]!.label
  let maxLabel = usable[0]!.label
  const consider = (value: number, label: string) => {
    if (value < min) {
      min = value
      minLabel = label
    }
    if (value > max) {
      max = value
      maxLabel = label
    }
  }
  for (const r of usable) {
    consider(clampOf(r.value), r.label)
    if (r.baseValue != null && isFiniteNumber(r.baseValue)) {
      consider(clampOf(r.baseValue), r.baseLabel ?? r.label)
    }
  }

  let lo: number
  let hi: number
  if (!hasBase && min >= 0) {
    lo = 0
    hi = max === 0 ? 1 : max * 1.06
  } else {
    const span = max - min || Math.abs(max) || 1
    lo = min - span * 0.08
    hi = max + span * 0.08
  }
  const range = hi - lo || 1
  const pct = (value: number) => ((value - lo) / range) * 100

  const plotted: RangePlotRow[] = usable.map((r, index) => {
    const x = pct(clampOf(r.value))
    const base =
      r.baseValue != null && isFiniteNumber(r.baseValue) ? pct(clampOf(r.baseValue)) : null
    return {
      tick: r.tick,
      xPct: x,
      label: r.label,
      baseXPct: base,
      baseLabel: base != null ? (r.baseLabel ?? null) : null,
      stemStartPct: base != null ? Math.min(base, x) : 0,
      stemEndPct: base != null ? Math.max(base, x) : x,
      note: r.note ?? null,
      sampleN: countOrNull(r.sampleN),
      // A base sample only means something on a row that HAS a base value; on
      // a lollipop it would render a parenthetical against nothing.
      sampleBaseN: base != null ? countOrNull(r.sampleBaseN) : null,
      clamped: clampMax != null && r.value > clampMax,
      index,
    }
  })

  const bands: RangePlot['bands'] = []
  for (const band of opts?.bands ?? []) {
    if (!isFiniteNumber(band.from) || !isFiniteNumber(band.to)) continue
    const bLo = Math.max(Math.min(band.from, band.to), lo)
    const bHi = Math.min(Math.max(band.from, band.to), hi)
    if (!(bHi > bLo)) continue
    bands.push({ xPct: pct(bLo), wPct: pct(bHi) - pct(bLo), label: band.label })
  }

  // Reference rule. Only drawn inside the domain: a reference the scale cannot
  // place would render at a false position, so it is dropped (the caller's
  // source note still carries the number).
  let ref: RangePlot['ref'] = null
  if (
    opts?.refValue != null &&
    isFiniteNumber(opts.refValue) &&
    opts.refLabel != null &&
    opts.refValue >= lo &&
    opts.refValue <= hi
  ) {
    ref = { xPct: pct(opts.refValue), label: opts.refLabel }
  }

  return {
    kind: 'range',
    rows: plotted,
    bands,
    ref,
    xMinLabel: minLabel,
    xMaxLabel: maxLabel,
  }
}

function barRefX(
  bars: readonly BarRect[],
  points: readonly PlotPointIn[],
  refValue: number,
): number | null {
  if (bars.length === 0 || points.length !== bars.length) return null
  const floors = points.map((p) => p.at)
  if (floors.some((v) => v == null || !Number.isFinite(v))) return null
  const first = floors[0]!
  if (refValue < first) return bars[0]!.x
  for (let i = 0; i < bars.length; i++) {
    const lo = floors[i]!
    const hi = i + 1 < floors.length ? floors[i + 1]! : Number.POSITIVE_INFINITY
    const inBand = i === bars.length - 1 ? refValue >= lo : refValue >= lo && refValue < hi
    if (!inBand) continue
    const bar = bars[i]!
    if (!Number.isFinite(hi) || hi <= lo) return bar.x + bar.w / 2
    const frac = Math.min(1, Math.max(0, (refValue - lo) / (hi - lo)))
    return bar.x + bar.w * frac
  }
  const last = bars[bars.length - 1]!
  return last.x + last.w
}

export function buildBarPlot(
  series: readonly PlotSeriesIn[],
  opts?: {
    layout?: 'vertical' | 'horizontal'
    highlightTicks?: readonly string[]
    baselineLabel?: string
    /**
     * Keep a zero as a zero-height bar. True for a run over time, where a step
     * with no value is part of the axis; false for categories, where a
     * category with nothing in it has no bar to draw.
     */
    keepZeros?: boolean
    /** Vertical rule. Needs `at` on each point as the band floor. */
    refValue?: number
    refLabel?: string
  },
): BarPlot | null {
  const layout = opts?.layout ?? 'vertical'
  const highlight = new Set(opts?.highlightTicks ?? [])
  const source = series[0]
  if (!source) return null
  const floor = opts?.keepZeros ? 0 : Number.MIN_VALUE
  const points = source.points.filter((p) => isFiniteNumber(p.value) && p.value >= floor)
  if (points.length < 1) return null

  let yMax = 0
  let yMaxLabel = points[0]!.label
  for (const p of points) {
    if (p.value > yMax) {
      yMax = p.value
      yMaxLabel = p.label
    }
  }
  if (!(yMax > 0)) return null
  // Bars grow from zero, so the rail's floor is zero — never the last bar's
  // own value, which read as an axis minimum (evaluator pass three, D3).
  const yMinLabel = opts?.baselineLabel ?? '0'

  const bars: BarRect[] = []
  const ticks: BarPlot['ticks'] = []

  if (layout === 'horizontal') {
    const rowH = 16
    const vbH = Math.max(VB_H, PAD.t + PAD.b + points.length * rowH)
    const plotW = VB_W - PAD.l - PAD.r
    const plotLeft = PAD.l
    points.forEach((p, i) => {
      const w = (p.value / yMax) * plotW
      const y = PAD.t + i * rowH
      bars.push({
        name: source.name,
        tick: p.tick,
        label: p.label,
        x: plotLeft,
        y,
        w: Math.max(w, 1),
        h: 10,
        index: i,
        highlight: highlight.has(p.tick),
      })
      ticks.push({ tick: p.tick, x: plotLeft, y: y + 8 })
    })
    return {
      kind: 'bars',
      layout,
      bars,
      yMinLabel,
      yMaxLabel,
      ticks,
      vbW: VB_W,
      vbH,
    }
  }

  const n = points.length
  const padL = 16
  const padR = 16
  const padB = BAR_PAD_B
  const plotW = VB_W - padL - padR
  const plotH = VB_H - PAD.t - padB
  const gap = plotW / n
  const barW = Math.max(4, gap * 0.62)
  points.forEach((p, i) => {
    const h = (p.value / yMax) * plotH
    const x = padL + i * gap + (gap - barW) / 2
    const y = PAD.t + plotH - h
    bars.push({
      name: source.name,
      tick: p.tick,
      label: p.label,
      x,
      y,
      w: barW,
      h: Math.max(h, 1),
      index: i,
      highlight: highlight.has(p.tick),
    })
    ticks.push({ tick: p.tick, x: x + barW / 2, y: VB_H - 6 })
  })

  const refX =
    opts?.refValue != null && opts.refLabel && opts.refLabel.trim().length > 0
      ? barRefX(bars, points, opts.refValue)
      : null
  if (refX != null && opts?.refValue != null) {
    const floors = points.map((p) => p.at)
    for (let i = 0; i < bars.length; i++) {
      const lo = floors[i]
      const hi = i + 1 < floors.length ? floors[i + 1] : Number.POSITIVE_INFINITY
      if (lo == null) continue
      const inBand = i === bars.length - 1 ? opts.refValue >= lo : opts.refValue >= lo && opts.refValue < (hi ?? Infinity)
      if (inBand) {
        bars[i]!.highlight = true
        break
      }
    }
  }

  return {
    kind: 'bars',
    layout,
    bars,
    yMinLabel,
    yMaxLabel,
    ticks,
    ref: refX != null && opts?.refLabel ? { x: refX, label: opts.refLabel } : undefined,
    vbW: VB_W,
    vbH: VB_H,
  }
}

export function buildMixPlot(
  series: readonly PlotSeriesIn[],
  opts?: { totalLabel?: string },
): MixPlot | null {
  const source = series[0]
  if (!source) return null
  const points = source.points.filter((p) => isFiniteNumber(p.value) && p.value > 0)
  if (points.length < 1) return null
  const total = points.reduce((sum, p) => sum + p.value, 0)
  if (!(total > 0)) return null

  const plotW = VB_W - PAD.l - PAD.r
  let x = PAD.l
  const segments: MixSegment[] = points.map((p, i) => {
    const w = (p.value / total) * plotW
    const seg = {
      name: source.name,
      tick: p.tick,
      label: p.label,
      x,
      w: Math.max(w, 1),
      index: i,
    }
    x += w
    return seg
  })

  return {
    kind: 'mix',
    segments,
    totalLabel: opts?.totalLabel ?? source.name,
    vbW: VB_W,
    vbH: MIX_H,
  }
}

/**
 * Tick positions for a line plot, from the caller's formatted ticks. Ticks
 * outside the plotted domain are dropped; the atom never invents a label.
 */
export function lineTicks(
  plot: LinePlot,
  yTicks: readonly PlotTickIn[] | undefined,
  xTicks: readonly PlotXTickIn[] | undefined,
): { y: { y: number; frac: number; label: string }[]; x: { x: number; frac: number; label: string }[] } {
  const { l, t, w, h, y0, y1, xMin, xMax } = plot.scale
  const yRange = y1 - y0 || 1
  const xSpan = xMax - xMin || 1
  const y = (yTicks ?? [])
    .filter((tk) => isFiniteNumber(tk.value) && tk.value >= y0 && tk.value <= y1 && tk.label.trim().length > 0)
    .map((tk) => {
      const frac = 1 - (tk.value - y0) / yRange
      return { y: t + frac * h, frac, label: tk.label }
    })
  const x = (xTicks ?? [])
    .filter((tk) => isFiniteNumber(tk.at) && tk.at >= xMin && tk.at <= xMax && tk.label.trim().length > 0)
    .map((tk) => {
      const frac = (tk.at - xMin) / xSpan
      return { x: l + frac * w, frac, label: tk.label }
    })
  return { y, x }
}

/* ==========================================================================
   THE ANSWER GEOMETRIES (site queue SITE-02b)

   Two shapes the four kinds above could not express, added because the place
   answer had to be DRAWN rather than tiled:

     - pair: two NAMED counts on ONE shared scale. `bars` draws one bar per
       point of one series and `mix` draws one strip whose segments sum to a
       whole; months of supply is neither. It is two independent counts — homes
       for sale, and homes that go under contract in a month — read against
       each other, which is what makes the division visible instead of printed.
     - strip: N marks on ONE shared horizontal axis, stacked into lanes where
       they collide. `range` draws one row per category; a distribution of
       comparable closes is N marks on one axis, and a single mark against a
       reference on a fixed rule (days to pending on 0 to 120) is the same
       geometry with one point and a declared domain.

   Both are PERCENT geometry, laid out in HTML for the same reason the range
   plot is: a stretched viewBox draws circles as ellipses.
   ========================================================================== */

/** One named bar of a pair. Counts, not shares — the geometry does the ratio. */
export type PairBarIn = {
  /** What this bar counts, as a person says it ("For sale right now"). */
  name: string
  value: number
  /** The count as the caller formatted it. */
  label: string
  /** The reading a hover or a tap reveals: window, population, definition. */
  note?: string
}

export type PairPlotBar = {
  name: string
  label: string
  note: string | null
  /** Share of the LARGER bar, 0 to 100. */
  pct: number
  /** True on the bar that set the scale. */
  leads: boolean
  index: number
}

export type PairPlot = {
  kind: 'pair'
  bars: PairPlotBar[]
  /** The larger count, as its own caller formatted it — the scale's ceiling. */
  maxLabel: string
}

/**
 * Two (or more) named counts on one shared scale.
 *
 * Widths are a share of the LARGEST bar, so the shorter one is read against the
 * taller one rather than against an invented ceiling. Fewer than two usable
 * bars returns null: a one-bar version of this drawing says nothing, because
 * the drawing IS the comparison.
 */
export function buildPairPlot(bars: readonly PairBarIn[]): PairPlot | null {
  const usable = bars.filter((b) => isFiniteNumber(b.value) && b.value >= 0)
  if (usable.length < 2) return null
  let max = 0
  let maxLabel = usable[0]!.label
  for (const b of usable) {
    if (b.value > max) {
      max = b.value
      maxLabel = b.label
    }
  }
  if (!(max > 0)) return null
  return {
    kind: 'pair',
    bars: usable.map((b, index) => ({
      name: b.name,
      label: b.label,
      note: b.note ?? null,
      pct: (b.value / max) * 100,
      leads: b.value === max,
      index,
    })),
    maxLabel,
  }
}

/* ==========================================================================
   SPARK: one short run of values as a line, no axes, no labels.

   The Ledger's reveal draws twelve months of closes under a row this way
   (site queue SITE-52). It is the smallest honest chart: the reader gets the
   shape of the run and its endpoint, and the figures stay in the row's text
   and its trace. Null is a month the source did not publish; it BREAKS the
   line (the path lifts and restarts) rather than being drawn as zero, and a
   run with fewer published points than `minPoints` is not drawn at all
   (DATA_GRAPHICS.md small-n rule: omit, do not pad). A published point with
   no published neighbour is a run of one, which a path cannot show (a lone
   "M" paints nothing), so it comes back in `dots` for the caller to mark: a
   caption that names a low or a high must be able to point at it.

   Absolute geometry in the caller's box, not percent: a spark is rendered at
   one fixed size so its endpoint mark stays a circle and its stroke a stroke.
   ========================================================================== */

export type SparkPlot = {
  kind: 'spark'
  /** SVG path data: one M…L… run per unbroken stretch of published points. */
  d: string
  /** The last published point, for the endpoint mark. */
  last: { x: number; y: number } | null
  /**
   * Every other published point with no published neighbour on either side:
   * the path draws nothing there, so the caller marks each one.
   */
  dots: { x: number; y: number }[]
  /** Published points drawn. */
  n: number
  min: number
  max: number
}

export function buildSparkPlot(
  values: readonly (number | null)[],
  opts: { w: number; h: number; pad?: number; minPoints?: number },
): SparkPlot | null {
  const pad = opts.pad ?? 2
  const minPoints = opts.minPoints ?? 2
  const points: { i: number; v: number }[] = []
  values.forEach((v, i) => {
    if (typeof v === 'number' && isFiniteNumber(v)) points.push({ i, v })
  })
  if (values.length < 2 || points.length < Math.max(2, minPoints)) return null
  let min = Infinity
  let max = -Infinity
  for (const p of points) {
    if (p.v < min) min = p.v
    if (p.v > max) max = p.v
  }
  const span = max - min
  const innerW = Math.max(0, opts.w - pad * 2)
  const innerH = Math.max(0, opts.h - pad * 2)
  const x = (i: number) => pad + (i / (values.length - 1)) * innerW
  // A flat run sits on the middle line rather than the floor: it is not zero.
  const y = (v: number) => (span > 0 ? pad + innerH - ((v - min) / span) * innerH : pad + innerH / 2)
  let d = ''
  let prev = -2
  for (const p of points) {
    const px = x(p.i).toFixed(1)
    const py = y(p.v).toFixed(1)
    d += p.i === prev + 1 ? ` L${px} ${py}` : `${d ? ' ' : ''}M${px} ${py}`
    prev = p.i
  }
  const tail = points[points.length - 1]!
  const dots: { x: number; y: number }[] = []
  points.forEach((p, j) => {
    if (j === points.length - 1) return
    const joined = (j > 0 && points[j - 1]!.i === p.i - 1) || points[j + 1]!.i === p.i + 1
    if (!joined) dots.push({ x: x(p.i), y: y(p.v) })
  })
  return { kind: 'spark', d, last: { x: x(tail.i), y: y(tail.v) }, dots, n: points.length, min, max }
}

/** One mark on a strip. `at` is the x value; every string is caller-formatted. */
export type StripPointIn = {
  /** Stable id. Never an address — the caller decides what is safe to carry. */
  id: string
  /** X position in the axis' own units (a month index, square feet, days). */
  at: number
  /** How this point's x reads ("July 2026"). */
  tick: string
  /** The full reading a hover, tap or arrow key reveals. */
  label: string
}

export type StripPlotPoint = {
  id: string
  /** Position along the track, 0 to 100. */
  xPct: number
  /** Collision lane. 0 is the baseline row; each step stacks one mark higher. */
  lane: number
  tick: string
  label: string
  index: number
}

export type StripPlot = {
  kind: 'strip'
  points: StripPlotPoint[]
  /** How many lanes the marks needed. At least 1. */
  lanes: number
  /** The context mark (a city median, a subject's own size); null when outside the domain. */
  ref: { xPct: number; label: string } | null
  /** Axis labels the caller formatted, dropped when they fall outside the domain. */
  ticks: { xPct: number; label: string }[]
  xMinLabel: string
  xMaxLabel: string
}

/**
 * N marks on one shared horizontal axis.
 *
 * DOMAIN. A declared min/max wins — the days-to-pending rule is 0 to 120 days
 * whatever the one mark on it reads, because a rule whose ends move with the
 * subject is not a rule. Without one the domain is the data's own extent
 * padded 6%, widened when every mark shares one x.
 *
 * LANES. Marks closer together than `laneGap` percent stack instead of
 * overprinting: the reader has to be able to count them, and six closes in one
 * month drawn as one dot is a lie about n. Greedy lowest-free-lane assignment
 * in x order, which is the beeswarm the dataviz skill lists among the forms.
 */
export function buildStripPlot(
  points: readonly StripPointIn[],
  opts?: {
    min?: number
    max?: number
    /** Percent of the track two marks must clear to share a lane. */
    laneGap?: number
    refValue?: number
    refLabel?: string
    ticks?: readonly PlotXTickIn[]
  },
): StripPlot | null {
  const usable = points.filter((p) => isFiniteNumber(p.at))
  if (usable.length < 1) return null

  const declaredMin = opts?.min != null && isFiniteNumber(opts.min) ? opts.min : null
  const declaredMax = opts?.max != null && isFiniteNumber(opts.max) ? opts.max : null

  let dataMin = Infinity
  let dataMax = -Infinity
  let minLabel = usable[0]!.tick
  let maxLabel = usable[0]!.tick
  for (const p of usable) {
    if (p.at < dataMin) {
      dataMin = p.at
      minLabel = p.tick
    }
    if (p.at > dataMax) {
      dataMax = p.at
      maxLabel = p.tick
    }
  }

  let lo: number
  let hi: number
  if (declaredMin != null && declaredMax != null && declaredMax > declaredMin) {
    lo = declaredMin
    hi = declaredMax
  } else {
    const span = dataMax - dataMin
    // One unit, not a fraction of the value, when every mark shares one x: on
    // an axis whose zero is arbitrary (a month index, a year) a percentage of
    // the absolute value is a domain thousands of units wide around one point.
    const pad = span > 0 ? span * 0.06 : 1
    lo = dataMin - pad
    hi = dataMax + pad
  }
  const range = hi - lo || 1
  const pct = (value: number) => Math.min(100, Math.max(0, ((value - lo) / range) * 100))

  const laneGap = opts?.laneGap != null && isFiniteNumber(opts.laneGap) ? opts.laneGap : 5
  const laneLastX: number[] = []
  const ordered = [...usable]
    .map((p, order) => ({ p, order }))
    .sort((a, b) => a.p.at - b.p.at || a.order - b.order)

  const plotted: StripPlotPoint[] = []
  for (const { p, order } of ordered) {
    const x = pct(p.at)
    let lane = 0
    while (lane < laneLastX.length && x - laneLastX[lane]! < laneGap) lane += 1
    laneLastX[lane] = x
    plotted.push({ id: p.id, xPct: x, lane, tick: p.tick, label: p.label, index: order })
  }

  const ticks = (opts?.ticks ?? [])
    .filter((t) => isFiniteNumber(t.at) && t.at >= lo && t.at <= hi && t.label.trim().length > 0)
    .map((t) => ({ xPct: pct(t.at), label: t.label }))

  // Same rule as every other reference in this file: a mark the scale cannot
  // place would draw at a false position, so it is dropped rather than pinned
  // to an edge it does not belong on.
  const ref =
    opts?.refValue != null &&
    isFiniteNumber(opts.refValue) &&
    opts.refLabel != null &&
    opts.refLabel.trim().length > 0 &&
    opts.refValue >= lo &&
    opts.refValue <= hi
      ? { xPct: pct(opts.refValue), label: opts.refLabel }
      : null

  return {
    kind: 'strip',
    points: plotted,
    lanes: Math.max(1, laneLastX.length),
    ref,
    ticks,
    xMinLabel: minLabel,
    xMaxLabel: maxLabel,
  }
}
